import L from 'leaflet';

// Single source of truth for the external-global-`L` workaround every Leaflet
// plugin needs. The plugins we use — `@geoman-io/leaflet-geoman-free`,
// `leaflet-routing-machine`, `leaflet-polylinedecorator` — ship UMD/IIFE
// bundles with Leaflet marked external-global: they read the GLOBAL `L` the
// moment they evaluate. Vite's ESM `import L from 'leaflet'` never sets that
// global, so any plugin that evaluates before this assignment throws
// `ReferenceError: L is not defined`.
export const ensureLeafletGlobal = (): void => {
  (globalThis as typeof globalThis & { L?: typeof L }).L = L;
};

// LOADER CONTRACT — every Leaflet plugin MUST be loaded through this factory.
// It:
//   1. calls `ensureLeafletGlobal()` synchronously, THEN
//   2. evaluates the plugin's dynamic `import()` (passed as a thunk).
// A dynamic import cannot be hoisted (static imports can) nor chunk-reordered
// ahead of step 1, so the global is guaranteed in place before the plugin
// evaluates — in dev and prod, regardless of bundler chunking.
//
// WHY A THUNK AND NOT A SPECIFIER STRING: the call site writes the literal
// `() => import('leaflet-routing-machine')`, so Rollup's static chunk analysis
// still sees the string specifier and gives each plugin its own lazily-
// evaluated async chunk. `import(specifier)` with a runtime variable would
// defeat that analysis (and resolve to a bare URL at runtime, breaking the
// prod build). Passing the global-set through the factory makes step 1
// structurally impossible to skip — the contract is enforced by the type, not
// by a convention a future loader author could forget.
export const createLeafletPluginLoader = <T>(
  load: () => Promise<T>
): Promise<T> => {
  ensureLeafletGlobal();
  return load();
};

// GUARDRAILS — so a new plugin can't silently reintroduce the crash:
//   - `biome.json` noRestrictedImports rejects a bare `import 'leaflet-*'` /
//     `import('leaflet-*')` anywhere except the allowlisted loader modules.
//   - `apps/web/src/shared/bundle/leaflet-plugin-imports.test.ts` is a generic
//     source scan that fails if ANY `leaflet-*` / `@geoman-io` import (or
//     `createLeafletPluginLoader(() => import('leaflet-*'))` call) appears
//     outside those same loader modules — covering future plugins Biome's
//     per-specifier rule can't list. It also asserts each allowlisted loader
//     actually routes through this factory, closing the "forgot step 1" gap.
//   - `shared/bundle/manual-chunks.ts` keeps each plugin out of the shared
//     `vendor-leaflet` chunk (eagerly evaluated) so the dynamic import keeps
//     its own lazy chunk; a unit test locks that mapping in place.
